<template>
    <div>
      <FileManagerModal
        :open="displayModal"
        :maxFiles="maxFiles"
        @onClose="displayModal = false"
        @onSelectedFileHandles="setFileHandles"
      />

      <div v-if="isValid">
        <!-- header -->
        <div class="row">
          <div  class="col-9">
            <p>
              <span class="fs-4">Evento: {{ eventName }} /</span>
              <span class="fs-5">Categoria: {{ categoryName }}</span>
            </p>
          </div>
          <div class="col-3 text-end">
            <button @click="displayModal = true" class="btn btn-primary" :disabled="hasFiles">
                {{$t("fileManager.tools.search")}}
            </button>
          </div>
          <hr />
        </div>

        <!-- table processing files  -->
        <div v-if="files.length" class="row">
          <!-- tools  -->
          <div class="col-12 text-end">
            <button v-if="showStopAction" 
              class="btn btn-info btn-sm"
              @click="onStop" 
            >
              {{$t("fileManager.tools.stop")}}
            </button>

            <button v-if="showUploadAction" 
              class="btn btn-success btn-sm"
              @click="onUpload" 
            >
              {{$t("fileManager.tools.upload")}}
            </button>
            
            <button v-if="showDeleteAction"
              class="btn btn-danger btn-sm"
              @click="onDestroy"
            >
              {{$t("fileManager.tools.clear")}}
            </button>
          </div>

          <div class="col-12">
            <table class="table table-striped tbl-file-manager-process">
                <thead class="table-light">
                  <tr>
                    <th scope="col" class="total">
                      {{ $t('fileManager.table.total') }}
                    </th>
                    <th scope="col" class="total">
                      {{ $t('fileManager.table.processed') }}
                    </th>
                    <th scope="col" class="total">
                      {{ $t('fileManager.table.done') }}
                    </th>
                    <th scope="col" class="total">
                      {{ $t('fileManager.table.erros') }}
                    </th>
                    <th scope="col" class="total">
                      {{ $t('fileManager.table.missings') }}
                    </th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="text-center">{{ totalFiles }}</td>
                    <td class="text-center">{{ totalProcessed }}</td>
                    <td class="text-center">{{ totalDone }}</td>
                    <td class="text-center">{{ totalErrors }}</td>
                    <td class="text-center">{{ totalMissings }}</td>
                    <td class="text-end">{{ timer }}</td>
                  </tr>
                </tbody>
              </table>
          </div>

          <div v-if="percentage > 0" class="col-12">
            <div class="progress">
              <div class="progress-bar"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                :style="percentageStyle"
              >
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <FileManagerTable 
              title="fileManager.pendingTable.title"
              description="fileManager.pendingTable.description"
              empty-message="fileManager.pendingTable.empty"
              :files="pendingFiles"
            />
          </div>

          <br />
          <hr />
          <br />

          <div v-if="hasErrors" class="col-12">
            <FileManagerTable 
              title="fileManager.errorsTable.title"
              description="fileManager.errorsTable.description"
              empty-message="fileManager.errorsTable.empty"              
              :files="errorFiles"
            />
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="!isSetupProcess" class="alert alert-danger" role="alert">
          {{ $t('fileManager.messages.notFound') }}
          <a href="/" class="alert-link">
            {{ $t('fileManager.messages.goToHome') }}
          </a>
        </div>
      </div>
  </div>
</template>

<script lang="js">
import moment from "moment"
import {mapGetters} from "vuex"
import { get, set, entries, del, createStore, update } from 'idb-keyval';
import { setChronometer, resetChronometer }  from "../../../shared/helpers/timer";
import FileManagerModal from './components/FileManagerModal.vue';
import FileManagerTable from './components/FileManagerTable.vue';
import { getFileSize, getBase64FileSize, resizeImage, getImageDimensions }  from "../../../shared/helpers/image";

/*
https://developer.chrome.com/articles/file-system-access/
https://css-tricks.com/getting-started-with-the-file-system-access-api/
https://developer.chrome.com/articles/origin-private-file-system/#creating-new-files-and-folders
https://developer.chrome.com/articles/origin-private-file-system/
sessionStorage.setItem(image.id, JSON.stringify(image));
https://2digitalstage.lvh.me:3001/file-manager/events/173/category/777
*/

let index = 0;

const FileStatuses = {
  Queue: 'queue',
  Processing: 'processing',
  ReadyToUpload: 'readyToUpload',
  Uploading: 'uploading',
  Error: 'error',
  Done: 'done'
}

const FileManagerStatuses = {
  Queue: 'queue',
  Uploading: 'uploading',
  Stopped: 'stopped',
  Done: 'done'
}

const PHOTO = {
  id: null,
  src: null,
  name: null,
  type: null,
  timestamp:  null,
  dimensions: null,
  originalSize: null,
  processedSize: null,
  status: FileStatuses.Queue
};

export default {
  name: "FileManager",

  components: {
    FileManagerModal,
    FileManagerTable
  },

  data() {
    return {
      identifier: null,

      // timer
      timerDelay: 1000,
      timerIntervalId: null,
      
      // process
      processDelay: 150,
      processWorkers: 0,
      processIntervalId: null,
      maxProcessParallel: 5,

      // uploader
      uploaderDelay: 400,
      uploaderWorkers: 0,
      uploadIntervalId: null,
      maxUploaderParallel: 3,

      // uploader preferences
      isValid: false,
      userId: null,
      eventId: null,
      categoryId: null,
      eventName: null,
      categoryName: null,
      isSetupProcess: false,

      // image preferences
      dpi: 72,
      height: 1200,
      width: 1800,

      // process
      timer: '',
      totalDone: 0,
      totalErrors: 0,
      totalPending: 0,
      totalProcessed: 0,
      status: FileManagerStatuses.Queue,
      
      // file handle
      files:  [],
      maxFiles: 3000,
      directoryHandle: {},

      // modal
      displayModal: false,

      // store
      store: null
    }
  },

  async mounted() {
    //await this.cleanDirectories();
    await this.setPreferences();
    await this.setDirectory();
    await this.readFiles();
    this.setTimer();
  },

  methods:  {
    async setPreferences() {
      this.isSetupProcess = true;
      this.userId = this.currentUser.id;
      this.eventId = this.$route.params.event_id,
      this.categoryId = this.$route.params.category_id;

      const fileManager = await this.fetchSetup();

      if(fileManager) {
        this.setIdentifier();
        const {image_size, dpi, event_name, category_name} = fileManager;

        this.store = createStore(this.identifier, 'keyval');
        this.dpi = parseInt(dpi);
        this.eventName = event_name;
        this.categoryName = category_name;
        this.isValid = true;

        if(image_size && image_size != '') {
          const sizes = image_size.split("x");
          this.width = parseInt(sizes[0]);
          this.height = parseInt(sizes[1]);
        }
      }
    },

    async setIdentifier() {
      const sessionKey = 'fileManagerProcess';
      let processID = sessionStorage.getItem(sessionKey);

      if(!processID) {
        processID = Date.now();
        sessionStorage.setItem(sessionKey, processID);
      }
      this.identifier = `event_${this.eventId}_cat_${this.categoryId}_${processID}`;
    },

    // TODO: Complete this clean
    async cleanDirectories() {
      const root = await navigator.storage.getDirectory();

      for await (const handle of root.values()) {
        if (handle.kind == "directory") {
          const chunks = handle.name.split('_');

          if(chunks.length == 5) {
            const timestamp = chunks[4];
            new Date(timestamp)
            console.log(handle.name, chunks, timestamp);
          }
        }
      }
    },

    async fetchSetup() {
      try {
        const response = await this.$http.get(`/api/v3/events/${this.eventId}/category/${this.categoryId}/file_manager`)

        if (response.status == 200 || response.status == 304) {
          return response.data.event;
        } else {
          return null;
        }
      } catch (e) {
        this.$alertify.error(this.$t('fileManager.messages.notFound'));
        return null;
      } finally {
        this.isSetupProcess = false;
      }
    },

    async setDirectory() {
      const dirName = this.identifier;
      const root = await navigator.storage.getDirectory();
      this.directoryHandle = await root.getDirectoryHandle(dirName, {create: true});
    },

    async readFiles() {
      const images = await entries(this.store);
      const arrFiles = [];

      for (const image of images) {
        const record = await get(image[0], this.store);
        arrFiles.push(record);
      }
      this.files = arrFiles;
      index = this.files.length;
    },

    async setFileHandles(fileHandles) {
      try {
        if(fileHandles.length <= this.maxFiles) {
          this.$root.$emit("showAdminLoader");

          for await (const fileHandle of fileHandles) { 
            await this.writeFile(fileHandle);
          }
          
        } else {
          this.$alertify.error(`El maxímo de imagenes que se pueden procesar son: # ${this.maxFiles}`);
        }
      } catch(error) {
        this.$alertify.error(error.message);
        console.log('Error', error);
      } finally {
        this.$root.$emit("hideAdminLoader");
      }
    },

    // Write a new file in OPFS
    async writeFile(fileHandle) {
      const file = await fileHandle.getFile();

      if(file.type.match('image.*')) {
        const newFile = await this.directoryHandle.getFileHandle(file.name, { create: true });
        const writable = await newFile.createWritable();       
        await writable.write(file);
        await writable.close();
        await this.addToIndexer(file);
      }
    },

    async reWriteFile(file) {
      const newFile = await this.directoryHandle.getFileHandle(file.name, { create: true });
      const writable = await newFile.createWritable();       
      await writable.write(file.src);
      await writable.close();
    },    

    // database
    deleteFile(file) {
      file.status = FileStatuses.Done;
      this.directoryHandle.removeEntry(file.name);
      del(file.id, this.store);
    },

    updateFile(file) {
      update(file.id, (_) => file, this.store);
    },     

    async addToIndexer(file) {
      index = index + 1;
      const id = `image-${index}`;

      const record = {
        ...PHOTO,
        id: id,
        name: file.name,
        type: file.type,
        originalSize: getFileSize(file.size),
        timestamp:  moment(file.lastModified).format("YYYYMMDD_HHmmss")
      }

      await set(id, record, this.store);
      this.files.push(record);
    },

    // process
    async proccessFile(file) {
      file.status = FileStatuses.Processing;
      const fileHandle = await this.directoryHandle.getFileHandle(file.name);
      const contentFile = await fileHandle.getFile();
      const source = URL.createObjectURL(await contentFile);

      file.dimensions = await getImageDimensions(source);
      file.src = await resizeImage(file.dimensions, source, this.width, this.height, this.dpi);
      file.processedSize = getBase64FileSize(file.src);

      file.status = FileStatuses.ReadyToUpload;

      this.reWriteFile(file);
      this.updateFile(file);
      this.processWorkers--;
    },

    async uploadFile(file) {
      try {
        file.status = FileStatuses.Uploading;

        const response = await this.$http.post(
            `${CHUSPIC_PHOTOS_API}/v1/photos`, 
            {
              user_id:   this.userId,
              event_id:  this.eventId,
              event_category_id: this.categoryId,
              filename:  file.name,
              timestamp: file.timestamp,
              image:     file.src,
            },
            {
              headers: {
                "Tenant": CURRENT_TENANT
              }
            }
        );

        if (response.status == 200 || response.status == 304) {
          this.totalDone++;
          this.deleteFile(file);
        }
      } catch (error) {
        console.log(`Error: ${error.message}`);
        file.status = FileStatuses.Error;
        this.updateFile(file);
        this.totalErrors++;
      } finally {
        this.totalPending--;
        this.uploaderWorkers--;
        this.totalProcessed++;
      }
    },

    async uploadChunk() {
      const files = this.readyToUploadFiles;

      if(files.length > 0 && this.isAvaliableUploaderWorker) {
        const file = files[0];
        this.uploaderWorkers++;
        this.uploadFile(file);
      }

      if(this.totalPending == 0) {
        this.onStop();
      }
    },

    async processChunk() {
      const files = this.queueFiles;
      
      if(this.isUploadingProcess && files.length > 0 && this.isAvaliableProcessWorker) {
        const file = files[0];
        this.processWorkers++;
        this.proccessFile(file)
      } 
      
      if(this.isStoppedProcess || files.length === 0) {
        clearInterval(this.processIntervalId);
      }
    },    

    // tools
    async onUpload() {
      this.status = FileManagerStatuses.Uploading;
      this.totalPending = this.pendingFiles.length;
      this.timerIntervalId = setInterval(this.setTimer, this.timerDelay);
      this.processIntervalId = setInterval(this.processChunk, this.processDelay);
      this.uploadIntervalId = setInterval(this.uploadChunk, this.uploaderDelay);
    },

    async onDestroy() {
      const images = await entries(this.store);
      this.totalErrors = 0;
      this.totalDone = 0;
      this.totalPending = 0;
      this.totalProcessed = 0;
      
      for (const image of images) {
        del(image[0], this.store);
      }
      this.files = [];
      index = this.files.length;
      
      await this.directoryHandle.remove({recursive: true});
      await this.setDirectory();
      this.timer = resetChronometer();
    },

    onStop() {
      this.status = FileManagerStatuses.Stopped;
      clearInterval(this.timerIntervalId);
      clearInterval(this.uploadIntervalId);
      clearInterval(this.processIntervalId);
    },

    setTimer() {
      this.timer = setChronometer();
    },

  },

  computed: {
    ...mapGetters('Auth', ['currentUser']),

    totalFiles() {
      return this.files.length;
    },

    pendingFiles() {
      return this.files.filter((record) => {
        return record.status != FileStatuses.Error && record.status != FileStatuses.Done;
      });
    },

    queueFiles() {
      return this.files.filter((record) => record.status == FileStatuses.Queue);
    },

    isUploadingFiles() {
      return this.files.filter((record) => record.status == FileStatuses.Uploading).length > 0;
    },

    isAvaliableUploaderWorker() {
      return this.uploaderWorkers < this.maxUploaderParallel;
    },

    isAvaliableProcessWorker() {
      return this.processWorkers < this.maxProcessParallel;
    },    

    readyToUploadFiles() {
      return this.files.filter((record) => record.status == FileStatuses.ReadyToUpload);
    },

    nextUploadFiles() {
      return this.readyToUploadFiles.slice(0, this.maxProcessParallel);
    },

    isUploadingProcess() {
      return this.status == FileManagerStatuses.Uploading;
    },

    isStoppedProcess() {
      return this.status == FileManagerStatuses.Stopped;
    },

    errorFiles() {
      return this.files.filter((record) => record.status == FileStatuses.Error);
    },

    showStopAction() {
      return this.status == FileManagerStatuses.Uploading && this.pendingFiles.length;
    },

    showUploadAction() {
      return (
        (this.status == FileManagerStatuses.Queue || this.status == FileManagerStatuses.Stopped)
          && this.pendingFiles.length
      );
    },

    showDeleteAction() {
      return this.files.length > 0 && !this.isUploadingProcess;
    },

    totalMissings() {
      return (this.totalFiles - this.totalProcessed);
    },

    percentage() {
      return ((100 * this.totalProcessed) / this.totalFiles).toFixed(2);
    },

    percentageStyle() {
      return `width: ${this.percentage}%;`;
    },

    hasFiles() {
      return this.totalFiles > 0;
    },

    hasErrors() {
      return this.errorFiles.length > 0;
    }
  }
}
</script>

<style lang="scss">
.tbl-file-manager-process {
  th.total {
    width: 140px;
  }
}
.progress {
  height: 20px;
}
</style>